import { useEffect, useState } from 'react';
import { getAuthToken } from '@/utils/authToken';
import { httpFetch } from '@/lib/httpClient';
import { reportError } from '@/observability/clientLogger';
import type { EmbeddedPage } from '@/types/embeddedPage';

export type EmbeddedPageState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; page: EmbeddedPage }
  | { status: 'error'; error: string };

/**
 * Loads the record behind /casefiles and /view/:id from /api/embedded-page, sending the
 * stored token as a Bearer header — the same path /api/me authenticates on. Idle unless
 * `enabled`.
 *
 * This was the iOS-only fallback for when getServerSideProps could not resolve a tenant
 * (the SIYA app's framed WKWebView never sends our cookies). With the server render gone
 * it is the only path, which removes the case where the two disagreed.
 *
 * Pages render inside AuthGuard, so by the time this runs the handover token has already
 * been captured into localStorage.
 */
export function useEmbeddedPage(href: string, enabled: boolean): EmbeddedPageState {
  const [state, setState] = useState<EmbeddedPageState>({ status: enabled ? 'loading' : 'idle' });

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        const authToken = getAuthToken();
        const response = await httpFetch(`/api/embedded-page?href=${encodeURIComponent(href)}`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
        const data = await response.json().catch(() => null);
        if (cancelled) return;

        if (response.ok && data?.label) {
          setState({ status: 'ready', page: data as EmbeddedPage });
        } else if (response.status === 404) {
          setState({ status: 'error', error: 'Page not found' });
        } else {
          // A 401 here is the "Tenant not found" of old: no credential reached the server
          // by any route. Recorded because the page otherwise just shows the message.
          reportError(new Error(data?.error || 'Embedded page request failed'), {
            event_detail: 'embedded_page_fetch_failed', status: response.status, href,
          });
          setState({
            status: 'error',
            error: response.status === 401 ? 'Tenant not found' : 'Failed to load page',
          });
        }
      } catch (error) {
        if (cancelled) return;
        reportError(error, { event_detail: 'embedded_page_fetch_failed', href });
        setState({ status: 'error', error: 'Failed to load page' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [href, enabled]);

  return state;
}
